import {
  DraggableDrawer,
  LicenseDetail,
  CarouselMessage,
  OsUpgrade
} from '@monorepo/shared/components';

import { useEffect, useState } from 'react';

import { Button, Grid, Row, Col, Stack, VStack } from 'rsuite';

import {
  UPGRADE_STATE,
  useAppSelector,
  useGetSystemDataQuery
} from '@monorepo/shared';

import { HomeElms, InstanceAction, server } from '../../config/const';

import { selectInstances } from '@monorepo/shared/store/instanceSlice';
import { selectCurrentGroup } from '@monorepo/shared/store/groupSlice';
import { selectAssets } from '@monorepo/shared/store/assetSlice';
import { selectInstancesExtraInfo } from '@monorepo/shared/store/instanceExtraSlice';
import { selectInstancesStateInfo } from '@monorepo/shared/store/instanceStateSlice';

import { SnapshotState } from '@monorepo/shared/components/SnapshotState';

import CheckRoundIcon from '@rsuite/icons/CheckRound';

import { t } from 'i18next';

interface IProps {
  rowData: Record<string, any>;
}

const InstanceInfo = ({ rowData }: IProps) => {
  const instances = useAppSelector(selectInstances);
  const instancesExtraInfo = useAppSelector(selectInstancesExtraInfo);
  const assets = useAppSelector(selectAssets);
  const currentGroup = useAppSelector(selectCurrentGroup);
  const instancesStateInfo = useAppSelector(selectInstancesStateInfo);

  const [instance, setInstance] = useState<Record<string, any>>({});
  const [asset, setAsset] = useState<Record<string, any>>({});
  const [openDetail, setOpenDetail] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showUpgradingInfo, setShowUpgradingInfo] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [extraInfo, setExtraInfo] = useState<Record<string, any>>({});
  const [isNoAccess, setIsNoAccess] = useState(false);
  const [stateInfo, setStateInfo] = useState<Record<string, any>>({});

  const { data: systemData } = useGetSystemDataQuery({
    server
  });

  useEffect(() => {
    const data = assets?.[currentGroup.id]?.[rowData?.account_id];

    if (data) {
      setAsset({ ...data });
    }
  }, [assets]);

  useEffect(() => {
    const data = instances?.[currentGroup.id]?.[rowData?.account_id];

    const extra = instancesExtraInfo?.[currentGroup.id]?.[rowData?.account_id];

    if (data) {
      setInstance({ ...data });

      if (data?.version_upgrade) {
        setExtraInfo({
          version_upgrade: data.version_upgrade
        });
      }

      setIsExpired(data?.is_expired);
      setIsNoAccess(data?.service_setting === 'NO_ACCESS');
    }

    if (extra) {
      if (extra?.is_active) {
        setExtraInfo(extra);
      }

      setIsActive(extra?.is_active);
    }
  }, [instances, currentGroup, instancesExtraInfo]);

  useEffect(() => {
    const info = instancesStateInfo?.[currentGroup.id]?.[rowData?.account_id];

    setShowUpgradingInfo(
      info?.showUpgrade || info?.state === UPGRADE_STATE.UPGRADE_SUCCESS
    );

    setStateInfo(info);
  }, [instancesStateInfo]);

  return (
    <VStack className="instance-info" alignItems="stretch">
      <VStack alignItems="stretch" spacing={10}>
        <Stack justifyContent="space-between">
          <b style={{ fontSize: 16 }}>{t('common.expandedRow.information')}</b>

          <SnapshotState stateInfo={stateInfo} />
        </Stack>

        <CarouselMessage
          license={instance?.detail?.license}
          systemData={systemData}
          upgradeInfo={{
            ...extraInfo?.version_upgrade,
            need_force_upgrade: extraInfo?.need_force_upgrade
          }}
          userType={rowData?.user_type}
        />

        <Grid fluid style={{ width: '100%' }}>
          <Row>
            <Col span={24}>
              <VStack spacing={0}>
                <Stack spacing={16} alignItems="center">
                  <div className="instance-detail-title">
                    {t('common.expandedRow.serviceDescription')}
                  </div>

                  <div>
                    <span>
                      {`${HomeElms?.name} Cloud `}
                      {instance?.has_soc_entitled ? 'with' : 'without'} SOCaaS
                    </span>
                  </div>
                </Stack>

                <Stack spacing={16} alignItems="center">
                  <div className="instance-detail-title">
                    {t('common.expandedRow.expirationDate')}
                  </div>

                  <Stack alignItems="center" spacing={10}>
                    <b style={{ lineHeight: '36px' }}>
                      {instance?.expiry_date}
                    </b>

                    {asset?.asset && (
                      <Button
                        appearance="link"
                        onClick={() => setOpenDetail(true)}
                        style={{ padding: 0 }}
                      >
                        {t('common.expandedRow.detail')}
                      </Button>
                    )}

                    <DraggableDrawer
                      className="api-portal-drawer"
                      open={openDetail}
                      setOpen={setOpenDetail}
                      size="lg"
                      title={<b>{t('common.expandedRow.licenseDetail')}</b>}
                    >
                      <LicenseDetail
                        data={asset?.asset}
                        systemData={systemData}
                      />
                    </DraggableDrawer>
                  </Stack>
                </Stack>

                <div style={{ width: '100%' }}>
                  <Stack
                    spacing={16}
                    style={{ width: '100%' }}
                    alignItems="center"
                  >
                    <div className="instance-detail-title">
                      {t('common.expandedRow.serviceVersion')}
                    </div>

                    <div style={{ minWidth: 235 }}>
                      <Stack
                        spacing={8}
                        style={{ height: 21 }}
                        alignItems="center"
                      >
                        <b>{instance?.firmware}</b>

                        {!isExpired && (isActive || showUpgradingInfo) && (
                          <OsUpgrade
                            isNoAccess={isNoAccess}
                            rowData={rowData}
                            extraInfo={extraInfo}
                            action={InstanceAction.upgrade}
                            server={server}
                            circle
                          />
                        )}
                      </Stack>
                    </div>
                  </Stack>
                </div>

                {instance?.fsoc_onboard && (
                  <div style={{ width: '100%' }}>
                    <Stack
                      spacing={16}
                      style={{ width: '100%' }}
                      alignItems="center"
                    >
                      <div className="instance-detail-title">
                        FSOC Onboarded
                      </div>

                      <div style={{ minWidth: 235 }}>
                        <Stack spacing={8} style={{ height: 15 }}>
                          <CheckRoundIcon style={{ color: '#52c41a' }} />
                        </Stack>
                      </div>
                    </Stack>
                  </div>
                )}

                {instance?.quotaHistoryData?.recommended_storage && (
                  <Stack spacing={16} alignItems="center">
                    <div className="instance-detail-title">
                      {t('common.expandedRow.recommendedStorage')}
                    </div>

                    <div>
                      <b>
                        {instance?.quotaHistoryData?.recommended_storage || 0}
                      </b>

                      <span>{` GB/${t('common.expandedRow.day')}`}</span>
                    </div>
                  </Stack>
                )}
              </VStack>
            </Col>
          </Row>
        </Grid>
      </VStack>
    </VStack>
  );
};

export default InstanceInfo;
